import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Copy, Link as LinkIcon, Trash2, RefreshCw, Lock, Radio,
  Users, MapPin, ExternalLink, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const TRACKING_POLL_MS = 12000;

/**
 * Dialog the coordinator opens from a plan page to manage the public share link.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (bool) => void
 *   planId: string
 */
export default function PlanShareDialog({ open, onOpenChange, planId }) {
  const { t } = useTranslation();
  const [existing, setExisting] = useState(null); // {token, link, expires_at, has_password}
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [allowChat, setAllowChat] = useState(true);
  const [allowTracking, setAllowTracking] = useState(true);
  const [tracking, setTracking] = useState({ live: [], arrivals: [] });

  // Look up existing share link for this plan when dialog opens — re-hydrate
  // from localStorage and verify it's still alive via /info.
  const lookup = useCallback(async () => {
    if (!planId) return;
    let cachedToken = null;
    try { cachedToken = localStorage.getItem(`photoplan:plan-share:${planId}`); } catch { /* noop */ }
    if (!cachedToken) return;
    try {
      const r = await api.get(`/share/${cachedToken}/info`);
      setExisting({
        token: cachedToken,
        link: `/p/${cachedToken}`,
        expires_at: r.data.expires_at,
        has_password: r.data.has_password,
      });
    } catch {
      try { localStorage.removeItem(`photoplan:plan-share:${planId}`); } catch { /* noop */ }
    }
  }, [planId]);

  useEffect(() => {
    if (open) {
      lookup();
    } else {
      setPassword('');
    }
  }, [open, lookup]);

  // Poll the coordinator-side tracking endpoint for live pins / arrivals.
  useEffect(() => {
    if (!open || !planId) return;
    const fetchTracking = async () => {
      try {
        const r = await api.get(`/share/plan/${planId}/tracking`);
        setTracking(r.data || { live: [], arrivals: [] });
      } catch {
        // ignore
      }
    };
    fetchTracking();
    const id = setInterval(fetchTracking, TRACKING_POLL_MS);
    return () => clearInterval(id);
  }, [open, planId]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const r = await api.post('/share/plan/generate', {
        plan_id: planId,
        password: password || null,
        allow_chat: allowChat,
        allow_tracking: allowTracking,
      });
      setExisting(r.data);
      try { localStorage.setItem(`photoplan:plan-share:${planId}`, r.data.token); } catch { /* noop */ }
      setPassword('');
      toast.success(t('planShare.passwordSet'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.linkUnavailable'));
    } finally {
      setLoading(false);
    }
  };

  const fullLink = existing
    ? `${window.location.origin}/p/${existing.token}`
    : '';

  const handleCopy = async () => {
    if (!fullLink) return;
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success(t('planShare.copied'));
    } catch {
      toast.error(t('planShare.uploadFailed'));
    }
  };

  const handleRevoke = async () => {
    if (!existing) return;
    if (!window.confirm(t('planShare.revokedConfirm'))) return;
    try {
      await api.delete(`/share/${existing.token}`);
      try { localStorage.removeItem(`photoplan:plan-share:${planId}`); } catch { /* noop */ }
      toast.success(t('planShare.revoked'));
      setExisting(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.linkUnavailable'));
    }
  };

  const handleResetPassword = async () => {
    if (!existing) return;
    try {
      await api.post(`/share/plan/${existing.token}/password`, { password: password || null });
      toast.success(password ? t('planShare.passwordSet') : t('planShare.passwordCleared'));
      setExisting({ ...existing, has_password: !!password });
      setPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.linkUnavailable'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="w-4 h-4 text-emerald-600" />
            {t('planShare.title')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">{t('planShare.description')}</p>

        {!existing ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">{t('planShare.passwordOptional')}</label>
              <Input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('planShare.passwordPlaceholder')}
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={allowChat} onChange={(e) => setAllowChat(e.target.checked)} />
                {t('planShare.allowChat')}
              </label>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={allowTracking} onChange={(e) => setAllowTracking(e.target.checked)} />
                {t('planShare.allowTracking')}
              </label>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              {t('planShare.generateLink')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={fullLink} readOnly className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={handleCopy}>
                  <Copy className="w-4 h-4" />
                </Button>
                <a href={fullLink} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  {existing.has_password ? t('planShare.password') : t('planShare.noPassword')}
                </span>
                {existing.expires_at && (
                  <span>· {t('planShare.expiresAt')}: {new Date(existing.expires_at).toLocaleString()}</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600">{t('planShare.resetPassword')}</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('planShare.passwordPlaceholder')}
                  className="flex-1"
                />
                <Button size="sm" variant="outline" onClick={handleResetPassword}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              onClick={handleRevoke}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t('planShare.revoke')}
            </Button>
          </div>
        )}

        {/* Live tracking widget */}
        <div className="border-t border-slate-200 pt-3 mt-2 space-y-2">
          <h4 className="text-xs font-semibold text-slate-600 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-emerald-600" />
            {t('planShare.tracking')}
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="border border-slate-200 rounded-lg p-2">
              <div className="flex items-center gap-1 text-slate-500 mb-1">
                <Users className="w-3 h-3" /> {t('planShare.online')}
              </div>
              {tracking.live.length === 0 ? (
                <p className="text-slate-400">{t('planShare.noLocations')}</p>
              ) : (
                <ul className="space-y-0.5">
                  {tracking.live.map((l) => (
                    <li key={l.id || l.photographer_id} className="truncate">
                      <Badge variant="outline" className="text-[10px] mr-1">
                        {l.mode === 'active' ? t('planShare.active') : t('planShare.passive')}
                      </Badge>
                      {l.photographer_id?.slice(0, 8)}…
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border border-slate-200 rounded-lg p-2">
              <div className="flex items-center gap-1 text-slate-500 mb-1">
                <CheckCircle2 className="w-3 h-3" /> {t('planShare.arrived')}
              </div>
              {tracking.arrivals.length === 0 ? (
                <p className="text-slate-400">{t('planShare.noArrivals')}</p>
              ) : (
                <ul className="space-y-0.5">
                  {tracking.arrivals.slice(0, 6).map((a) => (
                    <li key={a.id} className="truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-emerald-600" />
                      {a.spot_name || a.spot_id?.slice(0, 8)}
                      <span className="text-[10px] text-slate-400">
                        ({a.source === 'active' ? t('planShare.arrivedActive') : t('planShare.arrivedAuto')})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
